import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchAllCrmContacts, type AggregatedContact } from "@/lib/crmAggregate";
import { CrmEmptyState } from "@/components/crm/CrmEmptyState";
import { RecordLinkRow } from "@/components/crm/RecordLinkRow";
import { crmContactDisplayName, normalizeCrmContactKind } from "@/lib/crmContactParty";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";

export function CrmContactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim().toLowerCase();
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<AggregatedContact[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchAllCrmContacts(200);
        if (!cancelled) setContacts(rows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load contacts.");
          setContacts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredContacts = useMemo(
    () =>
      contacts.filter((contact) =>
        !query
          ? true
          : [
              contact.fullName,
              contact.firstName,
              contact.lastName,
              contact.email,
              contact.title,
              contact.accountId,
              contact.accountName,
              contact.linkedinUrl,
              contact.status,
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(query)),
      ),
    [contacts, query],
  );

  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Contacts</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Manage people records, communication history, and lifecycle stage details.
          </p>
        </div>
        <Link
          to="/crm/contacts/new"
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Create new contact
        </Link>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search contacts by name, email, company, or status"
          className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button
          type="submit"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Search
        </button>
      </form>

      {loading ? <p className="text-sm text-slate-500">Loading contacts…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
          CRM contacts are unavailable. {error}
        </div>
      ) : null}

      {!loading && !error && filteredContacts.length === 0 && !query ? (
        <CrmEmptyState
          title="No contacts yet"
          description="Add people to accounts so you can link them to deals. Create an account first if you do not have one, or convert a lead."
          primaryCta={{ href: "/crm/contacts/new", label: "Create your first contact" }}
          secondaryCta={{ href: "/crm/accounts/new", label: "Create an account" }}
        />
      ) : null}

      {!loading && (filteredContacts.length > 0 || query || error) ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Kind</th>
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => {
                const href = `/crm/contacts/${encodeURIComponent(contact.id)}?accountId=${encodeURIComponent(contact.accountId)}`;
                const name = crmContactDisplayName(contact);
                return (
                  <RecordLinkRow key={`${contact.accountId}-${contact.id}`} href={href}>
                    <td className="px-4 py-3 font-medium">
                      <Link to={href} className="text-indigo-700 hover:underline dark:text-indigo-300">
                        {name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 capitalize">{normalizeCrmContactKind(contact.kind)}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/crm/accounts/${encodeURIComponent(contact.accountId)}`}
                        className="text-indigo-700 hover:underline dark:text-indigo-300"
                        data-row-ignore
                      >
                        {contact.accountName || contact.accountId}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{contact.email || "—"}</td>
                    <td className="px-4 py-3">{contact.title || "—"}</td>
                    <td className="px-4 py-3">{formatCrmStatusLabel(contact.status) || "—"}</td>
                    <td className="px-4 py-3">
                      {contact.updatedAt ? new Date(contact.updatedAt).toLocaleString() : "—"}
                    </td>
                  </RecordLinkRow>
                );
              })}
              {filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                    {query ? "No matching contacts found." : "No contacts yet."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
